import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect, ConnectedProps, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import './providerSession.scss'

import { skipToken } from '@reduxjs/toolkit/query'
import { addMinutes, formatISO, subMinutes } from 'date-fns'
import { bindActionCreators } from 'redux'
import styled from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { translateFeedbackResponse } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { RECORDING_STATUS } from '@lyrahealth-inc/shared-app-logic/src/features/videoSession/constants'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { CalendarAppointmentsView } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { FeedbackEntityType } from '@lyrahealth-inc/shared-app-logic/src/models/feedback/Feedback'
import { SESSION_TYPES, USER_ROLES } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { VideoSession as VideoSessionData } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import SessionBanner from '@lyrahealth-inc/ui-core/lib/molecules/sessionBanner/SessionBanner'
import { ZoomProvider } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useZoom/index'
import { RecordConsentModal } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/recordConsentModal/RecordConsentModal'
import { FormChangeProperties } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/formBody/FormBody'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { VideoSessionFeedback } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/videoSessionFeedback/VideoSessionFeedback'
import { ZoomVideoSession } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/zoomVideoSession/ZoomVideoSession'

import { track } from '../../../../../mixpanel/mixpanelTracking'
import { getAuthConfig, getAuthIsLoggedIn, getAuthUser, getAuthUserId } from '../../../data/auth/authSelectors'
import { dismissInformedConsentBanner } from '../../../data/informedConsentBannerSlice'
import { RootState, useAppDispatch } from '../../../data/store'
import {
  useGetAppointmentsQuery,
  useUpdateAppointmentMutation,
} from '../../../lyraTherapy/clients/clientDetails/data/appointmentsApi'
import { useSelectedClientData } from '../../../lyraTherapy/clients/clientDetails/data/hooks/useSelectedClientData'
import { clearSelectedClientStore } from '../../../lyraTherapy/clients/clientDetails/data/ltClientDetailsAutoActions'
import { setSelectedClientId } from '../../../lyraTherapy/clients/clientDetails/data/ltClientSlice'
import { useClientsData } from '../../../lyraTherapy/clients/data/hooks/useClientsData'
import { useVideoSessionBanners } from '../../../lyraTherapy/consent/data/hooks/useVideoSessionBanners'
import { setToastContent } from '../../../lyraTherapy/data/ltToastAutoActions'
import { useVideoSessionForAppointment } from '../../../lyraTherapy/data/useVideoSessionForAppointment'
import {
  useCreateVideoSessionMutation,
  useLazyPingLTQuery,
  useSubmitVideoQualityFeedbackMutation,
  useUpdateVideoSessionMetadataMutation,
} from '../../../lyraTherapy/data/videoSessionApi'
import { setInformedConsentBannerHeight, setVideoSessionAppointment } from '../../../lyraTherapy/data/videoSessionSlice'
import {
  useGetEpisodeQuery,
  useUpdateEpisodeRecordingConsentMutation,
} from '../../../lyraTherapy/episodes/data/episodesApi'
import { getMessagesActiveLiveMsgAppt, inLiveMsgSession } from '../../../lyraTherapy/messages/data/messagesSelectors'
import { setActiveLiveMsgAppt, setLiveMsgClientIdToOpen } from '../../../lyraTherapy/messages/data/messagesSlice'
import { CLIENT_ACTIVITIES } from '../../constants/routingConstants'
import { logToSentry } from '../../utils/utils'
import JoinSessionBanner from '../joinSessionBanner/JoinSessionBanner'

const VideoSessionFeedbackContainer = styled.View({
  height: '100%',
})

type ProviderSessionProps = ConnectedProps<typeof connector>

const ProviderSession: FunctionComponent<ProviderSessionProps> = ({
  inLiveMsgSession,
  activeLiveMsgAppt,
  actions: { clearSelectedClientStore, setToastContent },
}) => {
  const user = useSelector(getAuthUser)
  const userId = useSelector(getAuthUserId)
  const config = useSelector(getAuthConfig)
  const [updateEpisodeRecordingConsent] = useUpdateEpisodeRecordingConsentMutation()
  const [submitVideoQualityFeedback] = useSubmitVideoQualityFeedbackMutation()
  const [pingLT] = useLazyPingLTQuery()
  const {
    isRecordingConsentBannerEnabled,
    isDAEngageOnOffPlatformVideoEnabled,
    isFiveMinuteVideoSessionWarningEnabled,
  } = useFlags()

  const isLoggedIn = useSelector(getAuthIsLoggedIn)
  const [zoomAppointment, setZoomAppointment] = useState<Appointment | null>(null)
  const [isZoomSessionStartPending, setIsZoomSessionStartPending] = useState(false)
  const [showRecordingModal, setShowRecordingModal] = useState(false)
  const [showCallQualityModal, setShowCallQualityModal] = useState(false)
  const refreshSessionId = useRef<number | null>(null)
  const navigate = useNavigate()
  const lastVideoSessionId = useRef<string>()
  const dispatch = useAppDispatch()
  const videoSession = useVideoSessionForAppointment(zoomAppointment?.appointmentId)
  const { data: episode } = useGetEpisodeQuery(
    videoSession?.episode_id ? { episodeId: videoSession.episode_id } : skipToken,
  )
  const episodeForSession = videoSession ? episode : null
  const [updateVideoSessionMetadata] = useUpdateVideoSessionMetadataMutation()
  const [createVideoSession] = useCreateVideoSessionMutation()

  const isRecording = videoSession?.recording_status === RECORDING_STATUS.not_started && !config?.recordingDisabled

  const { data: allAppointments = [] } = useGetAppointmentsQuery(
    {
      providerId: userId,
      durationRange: { before: { hours: 2 }, after: { hours: 4 } },
      view: CalendarAppointmentsView.CALENDAR,
    },
    { pollingInterval: 1000 * 60 * 60 * 3 },
  )
  const [updateAppointment] = useUpdateAppointmentMutation()
  const { data: activeClients } = useClientsData({ status: 'active' })
  const { data: clientDetails } = useSelectedClientData()
  const { showRecordingConsentBanner, informedConsentBanner: sessionInformedConsentBanner } = useVideoSessionBanners({
    videoSession,
    appointment: zoomAppointment,
    clientPronouns: clientDetails?.pronouns,
  })

  const onDismissInformedConsentBanner = useCallback(() => {
    if (zoomAppointment) {
      dispatch(dismissInformedConsentBanner(zoomAppointment.appointmentId))
    }
  }, [dispatch, zoomAppointment])
  const onInformedConsentBannerHeightChange = useCallback(
    (height: number) => dispatch(setInformedConsentBannerHeight(height)),
    [dispatch],
  )
  const informedConsentBanner = useMemo(
    () =>
      sessionInformedConsentBanner
        ? {
            status: sessionInformedConsentBanner.status,
            recipients: sessionInformedConsentBanner.recipients,
            onDismiss: onDismissInformedConsentBanner,
          }
        : undefined,
    [onDismissInformedConsentBanner, sessionInformedConsentBanner],
  )

  const appointments = useMemo(
    () => (allAppointments ?? []).filter((session) => ['newAppt', 'rescheduled'].includes(session.appointmentStatus)),
    [allAppointments],
  )

  useEffect(() => {
    return () => {
      if (refreshSessionId.current) {
        window.clearInterval(refreshSessionId.current)
      }
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const sessionNumber = useMemo(() => {
    if (zoomAppointment?.sessionNumber) {
      return zoomAppointment.sessionNumber
    }
    if (!videoSession) {
      return undefined
    }
    return appointments.find((appt) => appt.appointmentId === parseInt(videoSession?.appointment_id))?.sessionNumber
  }, [zoomAppointment?.sessionNumber, appointments, videoSession])

  const getTokenForSession: (data: {
    apptID: number
    participantID: string
    sessionNumber: number
  }) => Promise<VideoSessionData> = useCallback(
    ({ apptID, participantID, sessionNumber }) => {
      logToSentry('getVideoSessionToken', {
        stack: new Error('getVideoSessionToken call site').stack,
      })
      return createVideoSession({
        appointmentId: apptID,
        patientId: participantID,
        providerId: userId,
        sessionNumber: sessionNumber,
        ...(config?.recordingDisabled && { recording_status: RECORDING_STATUS.disabled }),
      }).unwrap()
    },
    [createVideoSession, userId, config?.recordingDisabled],
  )

  const sessionOpenedCallback = useCallback(() => {
    // ZendeskWidget owns chat visibility through the video-session Redux state.
    refreshSessionId.current = window.setInterval(
      () => {
        track({
          event: 'ping lyra therapy to keep token alive',
          page: 'video session',
          details: 'provider',
        })
        pingLT()
      },
      1000 * 60 * 5,
    )
  }, [pingLT])

  const onRecordSessionPressed = useCallback(() => {
    if (!videoSession) {
      return Promise.reject(new Error('Cannot start recording: no video session is loaded'))
    }
    return updateVideoSessionMetadata({
      ...videoSession,
      recording_status: RECORDING_STATUS.not_started,
      session_number: sessionNumber,
    }).unwrap()
  }, [videoSession, updateVideoSessionMetadata, sessionNumber])

  const disableRecording = useCallback(() => {
    if (!videoSession) {
      return Promise.reject(new Error('Cannot disable recording: no video session is loaded'))
    }
    return updateVideoSessionMetadata({
      ...videoSession,
      recording_status: RECORDING_STATUS.disabled,
      session_number: sessionNumber,
    }).unwrap()
  }, [videoSession, updateVideoSessionMetadata, sessionNumber])

  const disableEpisodeRecording = useCallback(() => {
    if (!episodeForSession) {
      return Promise.reject(new Error('Cannot disable episode recording: no episode is loaded for this session'))
    }
    return Promise.all([
      disableRecording(),
      updateEpisodeRecordingConsent({ id: episodeForSession.id, recording_consent: false }).unwrap(),
    ])
  }, [disableRecording, episodeForSession, updateEpisodeRecordingConsent])

  const submitQualityFeedback = useCallback(
    (responses: FormChangeProperties, videoSessionId?: string) => {
      submitVideoQualityFeedback({
        entity_type: FeedbackEntityType.VIDEO_SESSION,
        entity_id: videoSessionId,
        lyra_id: user?.id,
        user_type: 'provider',
        feedback_response: translateFeedbackResponse(responses.values),
      })
      setShowCallQualityModal(false)
    },
    [submitVideoQualityFeedback, user?.id],
  )

  const onJoin = useCallback(() => {
    const client = activeClients?.find((client) => client.id === activeLiveMsgAppt?.userId)
    if (client) {
      dispatch(setLiveMsgClientIdToOpen(activeLiveMsgAppt?.userId ?? null))
      // clear the selected client if navigating from one client directly to another, Client.js does not unmount
      if (clientDetails?.id !== client.id) {
        clearSelectedClientStore()
        dispatch(setSelectedClientId(client.id))
      }
      navigate(CLIENT_ACTIVITIES.route)
    }
  }, [activeClients, activeLiveMsgAppt?.userId, clearSelectedClientStore, clientDetails?.id, dispatch, navigate])

  const liveMsgAppts = useMemo(
    () =>
      appointments.filter(
        (appt) => appt.meetingFormat === 'live_messaging' && activeClients?.some(({ id }) => id === appt.userId),
      ),
    [activeClients, appointments],
  )

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const THIRTY_SECONDS_IN_MS = 30000

  useEffect(() => {
    if (!isFiveMinuteVideoSessionWarningEnabled && timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [isFiveMinuteVideoSessionWarningEnabled])

  const calculateTimeoutForWarning = useCallback(
    (appointment: Appointment) => {
      const fiveMinutesBeforeSessionEnd = subMinutes(
        addMinutes(getDateFromAppointment(appointment), appointment.appointmentDuration),
        5,
      )

      const timeUntilWarning = fiveMinutesBeforeSessionEnd.getTime() - new Date().getTime()

      // Only set timer if there's still time left and we're more than 30 seconds from the warning
      if (timeUntilWarning > THIRTY_SECONDS_IN_MS) {
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          setToastContent({
            text: '5 minutes left in this session',
            toastType: 'info',
            id: 'ProviderSession-end-of-session-alert',
          })
        }, timeUntilWarning)
      }
    },
    [setToastContent],
  )

  const onZoomSessionStarted = useCallback(() => {
    setIsZoomSessionStartPending(true)
  }, [])

  useEffect(() => {
    if (!isZoomSessionStartPending) {
      return
    }
    if (!videoSession || !zoomAppointment || !episodeForSession) {
      return
    }
    setIsZoomSessionStartPending(false)
    dispatch(setVideoSessionAppointment(zoomAppointment))
    if (isFiveMinuteVideoSessionWarningEnabled) {
      calculateTimeoutForWarning(zoomAppointment)
    }
    // Providers with recording disabled: always disable recording (role-based)
    if (episodeForSession.recording_consent === false || config?.recordingDisabled) {
      updateVideoSessionMetadata({
        ...videoSession,
        status: 'started',
        start_time: formatISO(new Date()),
        recording_status: RECORDING_STATUS.disabled,
        session_number: sessionNumber,
      })
    } else {
      updateVideoSessionMetadata({
        ...videoSession,
        status: 'started',
        start_time: formatISO(new Date()),
        session_number: sessionNumber,
      })
        .unwrap()
        .then(() => {
          setShowRecordingModal(true)
        })
    }
    dispatch(setSelectedClientId(zoomAppointment.userId))
    const isSessionEpisode =
      episodeForSession.id === videoSession.episode_id && episodeForSession.patient_id === zoomAppointment.userId
    navigate(
      isSessionEpisode && episodeForSession.provider_patient_id
        ? `${CLIENT_ACTIVITIES.route}?relId=${episodeForSession.provider_patient_id}`
        : CLIENT_ACTIVITIES.route,
    )
  }, [
    isZoomSessionStartPending,
    videoSession,
    zoomAppointment,
    calculateTimeoutForWarning,
    episodeForSession,
    dispatch,
    navigate,
    updateVideoSessionMetadata,
    sessionNumber,
    config?.recordingDisabled,
    isFiveMinuteVideoSessionWarningEnabled,
  ])

  const clearZoomSessionResources = useCallback(() => {
    setIsZoomSessionStartPending(false)
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }

    dispatch(setVideoSessionAppointment(null))
    if (refreshSessionId.current) {
      window.clearInterval(refreshSessionId.current)
    }
    refreshSessionId.current = null
  }, [dispatch])

  const onZoomSessionEnded = useCallback(() => {
    clearZoomSessionResources()
    if (!zoomAppointment || !videoSession) {
      setZoomAppointment(null)
      return
    }

    // only update session info if session was officially started (to account for "dismissed" sessions)
    if (videoSession.status === 'started') {
      lastVideoSessionId.current = videoSession.id
      setShowCallQualityModal(true)
      // update metadata for db appointment and video session
      updateVideoSessionMetadata({
        ...videoSession,
        status: 'finished',
        end_time: formatISO(new Date()),
        session_number: sessionNumber,
      })
        .unwrap()
        .then(() => {
          updateAppointment({
            providerId: userId,
            data: {
              ...zoomAppointment,
              appointmentStatus: 'concluded',
            },
          })
            .unwrap()
            .then(() => {
              setZoomAppointment(null)
            })
        })
    } else {
      setZoomAppointment(null)
    }
  }, [
    clearZoomSessionResources,
    sessionNumber,
    updateAppointment,
    updateVideoSessionMetadata,
    userId,
    videoSession,
    zoomAppointment,
  ])

  const onZoomSessionClosed = useCallback(() => {
    clearZoomSessionResources()
    setZoomAppointment(null)
  }, [clearZoomSessionResources])

  const getZoomSessionParams = useCallback(async () => {
    if (!zoomAppointment?.userInfo) {
      throw new Error('Not in zoom session')
    }

    const videoSession = await getTokenForSession({
      apptID: zoomAppointment.appointmentId,
      participantID: zoomAppointment.userInfo.lyraId,
      sessionNumber: zoomAppointment.sessionNumber,
    })

    return {
      token: videoSession.access_token,
      userName: videoSession.participant_identity ?? 'provider',
      sessionName: videoSession.room_name,
    }
  }, [getTokenForSession, zoomAppointment])

  const onActiveSession = useCallback(
    ({ appt }: { appt: Appointment | null }) => {
      dispatch(setActiveLiveMsgAppt(appt))
    },
    [dispatch],
  )

  if (!isLoggedIn) {
    return null
  }

  return (
    <ZoomProvider isProvider logger={logToSentry}>
      {!zoomAppointment && config?.videoSessionsEnabled && <JoinSessionBanner onZoomSessionJoin={setZoomAppointment} />}
      <SessionBanner
        onJoin={onJoin}
        userRole={USER_ROLES.PROVIDER}
        appts={liveMsgAppts}
        inSession={inLiveMsgSession}
        sessionType={SESSION_TYPES.MESSAGE}
        onActiveSession={onActiveSession}
        timeZone={(liveMsgAppts as any).timeZone}
      />
      <RecordConsentModal
        show={showRecordingModal && !isRecordingConsentBannerEnabled && !config?.recordingDisabled}
        onDontRecordEpisodePressed={disableEpisodeRecording}
        onDontRecordSessionPressed={disableRecording}
        onRecordSessionPressed={onRecordSessionPressed}
        onClose={() => setShowRecordingModal(false)}
      />
      <Modal
        visible={showCallQualityModal}
        onCloseEnd={() => {
          setShowCallQualityModal(false)
        }}
        onRequestClose={() => setShowCallQualityModal(false)}
        modalContents={
          <VideoSessionFeedbackContainer>
            <VideoSessionFeedback
              saveForm={(values: FormChangeProperties) => {
                submitQualityFeedback(values, lastVideoSessionId.current)
              }}
              scrollEnabled={true}
            />
          </VideoSessionFeedbackContainer>
        }
        scrollable
        scrollableModalWidth='100%'
        scrollableModalHeight='100%'
        disableBottomSheet
      />
      {zoomAppointment && (
        <ZoomVideoSession
          appointment={zoomAppointment}
          getSessionParams={getZoomSessionParams}
          participantName={`${zoomAppointment.userInfo?.firstName} ${zoomAppointment.userInfo?.lastName}`}
          participantFirstName={zoomAppointment.userInfo?.firstName}
          onSessionOpened={sessionOpenedCallback}
          onSessionStarted={onZoomSessionStarted}
          onSessionEnded={onZoomSessionEnded}
          onSessionClosed={onZoomSessionClosed}
          isRecording={isRecording}
          addToast={setToastContent}
          onDisableRecordingPressed={!config?.recordingDisabled ? disableRecording : undefined}
          disableMobileView
          hideNotRecordingBadge={isDAEngageOnOffPlatformVideoEnabled && config?.supportsOnOffPlatformVideo}
          showRecordingConsentBanner={showRecordingConsentBanner}
          informedConsentBanner={informedConsentBanner}
          onInformedConsentBannerHeightChange={onInformedConsentBannerHeightChange}
          recordSessionProps={{
            recordSession: onRecordSessionPressed,
            dontRecordSession: disableRecording,
            dontRecordEpisode: disableEpisodeRecording,
          }}
        />
      )}
    </ZoomProvider>
  )
}

type StateProps = {
  inLiveMsgSession: boolean
  activeLiveMsgAppt: Appointment | undefined
}

const mapStateToProps = (state: RootState): StateProps => {
  return {
    inLiveMsgSession: inLiveMsgSession(state),
    activeLiveMsgAppt: getMessagesActiveLiveMsgAppt(state),
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    actions: bindActionCreators(
      {
        clearSelectedClientStore,
        setToastContent,
      },
      dispatch,
    ),
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(ProviderSession)
